<!--
  Image With Checklist Feature Component

  A features section component that displays a heading, checklist items with checkmark icons on the left,
  and an image with overlay tooltips on the right side.

  This component adapts its colors based on the site_context prop to match different clinic themes.
-->
<template>
  <div class="flex flex-col gap-8 items-center justify-center overflow-hidden px-4 py-12 lg:px-16 lg:py-16 relative w-full bg-white rounded-3xl">
    <div class="flex flex-col lg:flex-row gap-6 lg:gap-8 items-center relative shrink-0 w-full max-w-[1200px] mx-auto">
      <!-- Left Side: Text Content with Checklist -->
      <div class="flex flex-col gap-6 lg:gap-12 items-start justify-center relative w-full lg:flex-1 lg:min-w-0">
        <!-- Heading Section -->
        <div class="flex items-start relative shrink-0 w-full">
          <div class="flex-1 flex flex-col items-start">
            <div class="flex flex-col gap-1 items-start leading-none not-italic relative shrink-0 w-full">
              <!-- Overline -->
              <div v-if="overlineText" class="flex flex-col justify-center relative shrink-0 text-[14px] tracking-[1px] uppercase w-full font-semibold">
                <p class="leading-5">{{ overlineText }}</p>
              </div>
              <!-- Main Heading -->
              <div class="flex flex-col justify-center relative shrink-0 text-[32px] lg:text-4xl tracking-tight w-full font-medium">
                <p class="leading-10 lg:leading-11">{{ headingText }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Checklist Items -->
        <div class="flex flex-col gap-5 items-start relative shrink-0 w-full">
          <!-- Checklist Item 1 -->
          <div class="flex gap-3 items-start relative rounded-xl shrink-0 w-full">
            <!-- Check Circle Icon -->
            <div class="overflow-hidden relative shrink-0 size-8">
              <svg class="size-full" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 4.8C9.8144 4.8 4.8 9.8144 4.8 16C4.8 22.1856 9.8144 27.2 16 27.2C22.1856 27.2 27.2 22.1856 27.2 16C27.2 9.8144 22.1856 4.8 16 4.8ZM21.2656 13.2656L15.0656 19.4656C14.9376 19.5936 14.768 19.6576 14.6 19.6576C14.432 19.6576 14.2624 19.5936 14.1344 19.4656L10.7344 16.0656C10.4784 15.8096 10.4784 15.3904 10.7344 15.1344C10.9904 14.8784 11.4096 14.8784 11.6656 15.1344L14.6 18.0688L20.3344 12.3344C20.5904 12.0784 21.0096 12.0784 21.2656 12.3344C21.5216 12.5904 21.5216 13.0096 21.2656 13.2656Z" :fill="checkIconColor" />
              </svg>
            </div>
            <!-- Item Text -->
            <p class="flex-1 leading-6 text-gray-900 text-base font-normal">
              {{ checklistItem1 }}
            </p>
          </div>

          <!-- Checklist Item 2 -->
          <div class="flex gap-3 items-start relative rounded-xl shrink-0 w-full">
            <!-- Check Circle Icon -->
            <div class="overflow-hidden relative shrink-0 size-8">
              <svg class="size-full" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 4.8C9.8144 4.8 4.8 9.8144 4.8 16C4.8 22.1856 9.8144 27.2 16 27.2C22.1856 27.2 27.2 22.1856 27.2 16C27.2 9.8144 22.1856 4.8 16 4.8ZM21.2656 13.2656L15.0656 19.4656C14.9376 19.5936 14.768 19.6576 14.6 19.6576C14.432 19.6576 14.2624 19.5936 14.1344 19.4656L10.7344 16.0656C10.4784 15.8096 10.4784 15.3904 10.7344 15.1344C10.9904 14.8784 11.4096 14.8784 11.6656 15.1344L14.6 18.0688L20.3344 12.3344C20.5904 12.0784 21.0096 12.0784 21.2656 12.3344C21.5216 12.5904 21.5216 13.0096 21.2656 13.2656Z" :fill="checkIconColor" />
              </svg>
            </div>
            <!-- Item Text -->
            <p class="flex-1 leading-6 text-gray-900 text-base font-normal">
              {{ checklistItem2 }}
            </p>
          </div>

          <!-- Checklist Item 3 -->
          <div class="flex gap-3 items-start relative rounded-xl shrink-0 w-full">
            <!-- Check Circle Icon -->
            <div class="overflow-hidden relative shrink-0 size-8">
              <svg class="size-full" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 4.8C9.8144 4.8 4.8 9.8144 4.8 16C4.8 22.1856 9.8144 27.2 16 27.2C22.1856 27.2 27.2 22.1856 27.2 16C27.2 9.8144 22.1856 4.8 16 4.8ZM21.2656 13.2656L15.0656 19.4656C14.9376 19.5936 14.768 19.6576 14.6 19.6576C14.432 19.6576 14.2624 19.5936 14.1344 19.4656L10.7344 16.0656C10.4784 15.8096 10.4784 15.3904 10.7344 15.1344C10.9904 14.8784 11.4096 14.8784 11.6656 15.1344L14.6 18.0688L20.3344 12.3344C20.5904 12.0784 21.0096 12.0784 21.2656 12.3344C21.5216 12.5904 21.5216 13.0096 21.2656 13.2656Z" :fill="checkIconColor" />
              </svg>
            </div>
            <!-- Item Text -->
            <p class="flex-1 leading-6 text-gray-900 text-base font-normal">
              {{ checklistItem3 }}
            </p>
          </div>
        </div>
      </div>

      <!-- Mobile: doctor on right, tooltips stacked on left (Figma KLNP mobile) -->
      <div class="relative lg:hidden h-[343px] w-full overflow-hidden rounded-3xl">
        <div :class="`absolute inset-0 ${backgroundColorClass}`" />

        <div v-if="mainImage" class="absolute inset-y-0 right-0 w-[62%] overflow-hidden rounded-br-3xl">
          <img
            :src="mainImage"
            alt=""
            class="absolute inset-0 size-full object-cover object-[20%_center] pointer-events-none"
          >
        </div>

        <div class="absolute inset-y-0 left-3 z-10 flex w-[58%] flex-col justify-between py-8">
          <div v-if="tooltip1Text" class="speech-bubble">
            <div class="speech-bubble__content">
              <p class="text-sm font-semibold leading-5 text-gray-900 whitespace-pre-line">
                {{ tooltip1Text }}
              </p>
            </div>
            <div class="speech-bubble__tail" aria-hidden="true" />
          </div>

          <div v-if="tooltip2Text" class="speech-bubble">
            <div class="speech-bubble__content">
              <p class="text-sm font-semibold leading-5 text-gray-900 whitespace-pre-line">
                {{ tooltip2Text }}
              </p>
            </div>
            <div class="speech-bubble__tail" aria-hidden="true" />
          </div>

          <div v-if="tooltip3Text" class="speech-bubble">
            <div class="speech-bubble__content">
              <p class="text-sm font-semibold leading-5 text-gray-900 whitespace-pre-line">
                {{ tooltip3Text }}
              </p>
            </div>
            <div class="speech-bubble__tail" aria-hidden="true" />
          </div>
        </div>
      </div>

      <!-- Desktop: image with positioned tooltips -->
      <div class="relative hidden lg:block w-full lg:flex-1 lg:min-w-0 aspect-square">
        <!-- Background placeholder -->
        <div :class="`rounded-3xl size-full ${backgroundColorClass}`" />

        <!-- Main Image -->
        <div v-if="mainImage" class="absolute inset-0 rounded-3xl overflow-hidden">
          <img :src="mainImage" alt="" class="absolute inset-0 size-full object-cover object-center pointer-events-none">
        </div>

        <!-- Tooltip 1 - Top Right -->
        <div v-if="tooltip1Text" class="absolute left-[62%] top-[6.3%]">
          <div class="speech-bubble max-w-[200px]">
            <div class="speech-bubble__content">
              <p class="text-sm font-semibold leading-5 text-gray-900 whitespace-pre-line">
                {{ tooltip1Text }}
              </p>
            </div>
            <div class="speech-bubble__tail" aria-hidden="true" />
          </div>
        </div>

        <!-- Tooltip 2 - Left Middle -->
        <div v-if="tooltip2Text" class="absolute left-[-2.7%] top-[28.4%]">
          <div class="speech-bubble max-w-[200px]">
            <div class="speech-bubble__content">
              <p class="text-sm font-semibold leading-5 text-gray-900 whitespace-pre-line">
                {{ tooltip2Text }}
              </p>
            </div>
            <div class="speech-bubble__tail" aria-hidden="true" />
          </div>
        </div>

        <!-- Tooltip 3 - Right Lower -->
        <div v-if="tooltip3Text" class="absolute left-[64%] top-[34.9%]">
          <div class="speech-bubble max-w-[200px]">
            <div class="speech-bubble__content">
              <p class="text-sm font-semibold leading-5 text-gray-900 whitespace-pre-line">
                {{ tooltip3Text }}
              </p>
            </div>
            <div class="speech-bubble__tail" aria-hidden="true" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // eslint-disable-next-line vue/prop-name-casing
  site_context: {
    type: String,
    default: null,
  },
  overlineText: {
    type: String,
    default: 'KLINIKA PRZYCZYNOWEGO LECZENIA NIEPŁODNOŚCI'
  },
  headingText: {
    type: String,
    default: 'Jak działamy?'
  },
  checklistItem1: {
    type: String,
    default: 'Oferujemy program leczenia zawierający pakiet badań diagnostycznych i konsultacji medycznych podzielony na 3 etapy. Po zakończeniu danego etapu następuje podsumowanie uzyskanych efektów i zapoznanie pacjenta z dalszym postępowaniem w następnym etapie leczenia.'
  },
  checklistItem2: {
    type: String,
    default: 'Każda para biorąca udział w naszym programie leczenia niepłodności posiada opiekuna pacjenta (osobę wspomagającą, towarzyszącą na poszczególnych etapach) oraz lekarza prowadzącego i instruktora naturalnej metody obserwacji cyklu płodności).'
  },
  checklistItem3: {
    type: String,
    default: 'Stosujemy podejście interdyscyplinarne. W razie potrzeby w procesie leczenia zaangażowani są dodatkowi specjaliści (np. internista, ginekolog, endokrynolog, urolog, androlog, psycholog, dietetyk).'
  },
  mainImage: {
    type: String,
    default: null
  },
  tooltip1Text: {
    type: String,
    default: 'Pakiety diagnostyczne i konsultacje.'
  },
  tooltip2Text: {
    type: String,
    default: 'Otrzymujesz opiekuna\noraz lekarza prowadzącego.'
  },
  tooltip3Text: {
    type: String,
    default: 'Dodatkowi specjaliści zaangażowani\nw Twój program leczenia.'
  }
})

// Color mapping based on site_context
const backgroundColorClass = computed(() => {
  const colorMap = {
    'infertility_treatment_clinic': 'bg-infertility-clinic-secondary',
    'family_clinic': 'bg-family-clinic-secondary',
    'institute': 'bg-institute-secondary',
  }
  return colorMap[props.site_context] || 'bg-infertility-clinic-secondary'
})

// Figma "Media / Icon / Filled / check-circle" is #262626 in every context —
// the check icons are a neutral dark, not the light brand tint.
const checkIconColor = '#262626'
</script>

<style scoped>
.speech-bubble {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  filter: drop-shadow(0 2px 3px rgb(16 24 40 / 0.06));
}

.speech-bubble__content {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
}

.speech-bubble__tail {
  width: 0;
  height: 0;
  margin-left: 12px;
  margin-top: -1px;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid #fff;
}
</style>

